import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { has, each, filter } from 'lodash';

import { ServiceFunctionsAddModalComponent } from './modals/service-functions-add/service-functions-add.component';
import { ServiceFunctionsEditModalComponent } from './modals/service-functions-edit/service-functions-edit.component';
import { ConfirmModalComponent } from '../../../shared/modals/confirm-modal/confirm-modal.component';
import { UsersService } from '../users.service';
import { ServiceFunction, USERS_PATHS } from '../users';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { Enums } from '../../../configs/Enums';

interface ServiceFunctionsStateInterface {
  sf_id: string | number | null;
  is_visible_hidden_service_functions: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-services-functions',
  templateUrl: './service-functions.component.html',
  styleUrls: ['./service-functions.component.scss'],
  standalone: false,
})
export class ServiceFunctionsComponent implements OnInit {
  public functionId: number;
  public serviceFunctionsLoading: boolean;
  public editServiceFunctionModal: NgbModalRef;
  public serviceFunctions: ServiceFunction[];
  public serviceFunctionsFilteredList: ServiceFunction[];
  public serviceFunctionSelected: ServiceFunction | null;
  public isShowHiddenServiceFunctions: number;
  public isActiveShowAllFunctionsButton: boolean;
  public countShowServiceFunctionsPartial: number;
  public functionVersions: ServiceFunction[];
  public stateParams: ServiceFunctionsStateInterface = {
    sf_id: null,
    is_visible_hidden_service_functions: 0,
  };

  public serviceFunctionsBreadcrumbs = [{ text: 'Служебные функции сотрудников', url: null }];

  constructor(
    public modalService: NgbModal,
    public route: ActivatedRoute,
    public router: Router,
    private breadcrumbsService: BreadcrumbsService,
    private usersService: UsersService
  ) {}

  ngOnInit() {
    this.countShowServiceFunctionsPartial = 25;
    this.isShowHiddenServiceFunctions = 0;

    this.breadcrumbsService.updateBreadcrumbs(this.serviceFunctionsBreadcrumbs);
    this.route.params.subscribe((params) => {
      let countParameters = 0;
      let countExistsParameters = 0;

      this.functionId = params['sf_id'] ? +params['sf_id'] : null;
      this.serviceFunctionSelected = null;
      this.stateParams.sf_id = this.functionId || '';
      this.stateParams.is_visible_hidden_service_functions =
        params['is_visible_hidden_service_functions']
          ? +params['is_visible_hidden_service_functions']
          : 0;
      this.isShowHiddenServiceFunctions = this.stateParams.is_visible_hidden_service_functions;

      each(this.stateParams, function (value, key) {
        countParameters++;

        if (has(params, [key])) {
          countExistsParameters++;
        }
      });

      if (countParameters === countExistsParameters) {
        this.getServiceFunctions();
      } else {
        this.router.navigate([USERS_PATHS.service_functions, this.stateParams], {
          replaceUrl: true,
        });
      }
    });
  }

  filterFunctionsList() {
    let arr = [].concat(this.serviceFunctions || []);

    if (arr && !this.isShowHiddenServiceFunctions) {
      arr = filter(arr, (func) => func && func.is_visible === 1);
    }

    if (arr && this.serviceFunctionSelected) {
      arr = filter(arr, (func) => func.sf_id === this.serviceFunctionSelected.sf_id);
    }

    if (arr && arr.length > this.countShowServiceFunctionsPartial && !this.isActiveShowAllFunctionsButton) {
      this.isActiveShowAllFunctionsButton = false;
      arr = arr.slice(0, this.countShowServiceFunctionsPartial);
    } else {
      this.isActiveShowAllFunctionsButton = true;
    }

    return arr;
  }

  filterFunctionsListQueryParam() {
    let arr = [].concat(this.serviceFunctions || []);
    arr = filter(arr, (func) => func.sf_id === this.functionId);

    if (arr && arr.length === 1) {
      this.serviceFunctionSelected = arr[0];
    }

    return arr;
  }

  getServiceFunctions() {
    this.serviceFunctionsLoading = true;

    this.usersService.getServiceFunctions().subscribe(
      (response) => {
        this.serviceFunctions = response as ServiceFunction[];
        this.checkAndInitSelectedServiceFunction();

        if (this.functionId) {
          this.serviceFunctionsFilteredList = this.filterFunctionsListQueryParam();
        } else {
          this.serviceFunctionsFilteredList = this.filterFunctionsList();
        }

        this.serviceFunctionsLoading = false;
      },
      (error) => {
        console.log(error);
        this.serviceFunctionsLoading = false;
      }
    );
  }

  checkAndInitSelectedServiceFunction() {
    if (this.serviceFunctions && this.functionId) {
      let arr = filter(this.serviceFunctions, (func) => func.sf_id === this.functionId);

      if (arr && arr.length === 1) {
        this.serviceFunctionSelected = arr[0];
      }
    }
  }

  onOpenServiceFunctionDialog(func) {
    this.usersService.getServiceFunction(func.sf_id).subscribe(
      (response) => {
        this.functionVersions = response['data'];
        this.onOpenServiceFunctionEditDialog();
      },
      (error) => {
        console.log(error);
      }
    );
  }

  onApplyServiceFunctionsFilter(functionSelected: ServiceFunction) {
    this.stateParams.sf_id = functionSelected && functionSelected.sf_id;

    this.router.navigate([USERS_PATHS.service_functions, this.stateParams]);
  }

  onResetServiceFunctionsFilter() {
    this.stateParams.sf_id = '';

    this.router.navigate([USERS_PATHS.service_functions, this.stateParams]);
  }

  onChangeVisibleHiddenServiceFunctions(event) {
    this.isShowHiddenServiceFunctions = +event.target.checked;
    this.stateParams.is_visible_hidden_service_functions = +event.target.checked;

    this.router.navigate([USERS_PATHS.service_functions, this.stateParams]);
  }

  onChangeVisibilityAllFunctions() {
    this.isActiveShowAllFunctionsButton = true;
    this.serviceFunctionsFilteredList = this.filterFunctionsList();
  }

  onOpenAddServiceFunction() {
    const addServiceFunctionModal = this.modalService.open(ServiceFunctionsAddModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper modal-wrapper--w-600',
      backdrop: 'static',
    });

    addServiceFunctionModal.result.then(
      () => {
        this.getServiceFunctions();
      },
      () => {}
    );
  }

  onOpenServiceFunctionEditDialog() {
    const editServiceFunctionModal = this.modalService.open(ServiceFunctionsEditModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper modal-wrapper--w-600',
      backdrop: 'static',
    });

    editServiceFunctionModal.componentInstance.functionVersions = this.functionVersions;
    editServiceFunctionModal.result.then(
      () => {
        this.getServiceFunctions();
      },
      () => {}
    );
  }

  onOpenServiceFunctionConfirmDialog(func: ServiceFunction) {
    this.editServiceFunctionModal = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    this.editServiceFunctionModal.componentInstance.title = 'Подтвердите отключение';
    this.editServiceFunctionModal.componentInstance.description =
      'Служебная функция “' +
      func.service_function_name +
      '” будет скрыта.' +
      ' Вы не сможете использовать ее при регистрации / редактировании нового сотрудника. Вы действительно хотите' +
      ' скрыть эту запись?';
    this.editServiceFunctionModal.componentInstance.successButtonName = 'Да, отключить';
    this.editServiceFunctionModal.componentInstance.abortButtonName = 'Отмена';
    this.editServiceFunctionModal.componentInstance.data = { func: func };
    this.editServiceFunctionModal.componentInstance.successCallbackAction = this.changeStateServiceFunction.bind(this);
    this.editServiceFunctionModal.componentInstance.errorMessageCode = Enums.errorCodesMaps['BACKEND_ERROR'];
    this.editServiceFunctionModal.result.then(
      () => {
        this.getServiceFunctions();
      },
      () => {}
    );
  }

  onChangeStateServiceFunction(func: ServiceFunction) {
    if (func.is_visible) {
      this.onOpenServiceFunctionConfirmDialog(func);
    } else {
      this.changeStateServiceFunction({ func: func }).subscribe(() => {
        this.getServiceFunctions();
      });
    }
  }

  changeStateServiceFunction(data) {
    return this.usersService.updateServiceFunctionStatus(data.func.sf_id, {
      is_visible: +!data.func.is_visible,
    });
  }
}
